import * as THREE from 'three';

/**
 * @typedef {Object} EntityOptions
 * @property {THREE.Vector3} [position] - Initial position
 * @property {number} [rotation] - Initial Y-axis rotation in radians
 * @property {number} [collisionRadius] - Collision radius for physics
 * @property {boolean} [collidable] - Whether this entity can collide with others
 */

/**
 * Base class for all game entities.
 * Manages a Three.js Group and provides lifecycle hooks.
 */
export class Entity {
    /** @type {THREE.Group} */
    #mesh;

    /** @type {number} */
    #collisionRadius;

    /** @type {boolean} */
    #collidable;

    /**
     * @param {EntityOptions} options
     */
    constructor(options = {}) {
        this.#mesh = new THREE.Group();
        this.#collisionRadius = options.collisionRadius ?? 1.0;
        this.#collidable = options.collidable ?? true;

        if (options.position) {
            this.#mesh.position.copy(options.position);
        }
        if (options.rotation !== undefined) {
            this.#mesh.rotation.y = options.rotation;
        }
    }

    /** @returns {THREE.Group} */
    get mesh() {
        return this.#mesh;
    }

    /** @returns {number} */
    get collisionRadius() {
        return this.#collisionRadius;
    }

    /** @returns {boolean} */
    get collidable() {
        return this.#collidable;
    }

    /** @returns {THREE.Vector3} */
    get position() {
        return this.#mesh.position;
    }

    /** @returns {number} */
    get rotation() {
        return this.#mesh.rotation.y;
    }

    /** @param {number} value */
    set rotation(value) {
        this.#mesh.rotation.y = value;
    }

    /**
     * Called every frame.
     * @param {number} delta - Time since last frame in seconds
     */
    update(delta) {
        // Override in subclasses
    }

    /**
     * Called when entity is added to the scene.
     * @param {THREE.Scene} scene
     */
    onAdd(scene) {
        scene.add(this.#mesh);
    }

    /**
     * Called when entity is removed from the scene.
     * @param {THREE.Scene} scene
     */
    onRemove(scene) {
        scene.remove(this.#mesh);
    }

    /**
     * Dispose of Three.js resources.
     * Should be called when the entity is no longer needed.
     */
    dispose() {
        this.#mesh.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.geometry.dispose();
                if (Array.isArray(child.material)) {
                    child.material.forEach(m => m.dispose());
                } else {
                    child.material.dispose();
                }
            }
        });
    }
}
